// Import dependencies
import { unsafeCSS, css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';

// Import utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, sprintf } from '@wordpress/i18n';

// Import style for web component
import styles from './web-component-style.scss?inline';

const statusesData = {
	completed: {
		label: escapeHTML( __( 'Completed', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-log-status-completed',
	},
	'completed-do-nothing': {
		label: escapeHTML( __( 'Completed, do nothing', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-log-status-completed-do-nothing',
	},
	'completed-did-nothing': {
		label: escapeHTML( __( 'Completed, did nothing', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-log-status-completed-did-nothing',
	},
	cancelled: {
		label: escapeHTML( __( 'Cancelled', 'uncanny-automator' ) ),
		icon: 'times-circle',
		colorVariable: '--uap-log-status-cancelled',
	},
	paused: {
		label: escapeHTML( __( 'Paused', 'uncanny-automator' ) ),
		icon: 'pause',
		colorVariable: '--uap-log-status-paused',
	},
	ended: {
		label: escapeHTML( __( 'Ended', 'uncanny-automator' ) ),
		icon: 'stop',
		colorVariable: '--uap-log-status-ended',
	},
	'completed-with-notice': {
		label: escapeHTML( __( 'Completed with notice', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-log-status-completed-with-notice',
	},
	'skipped': {
		label: escapeHTML( __( 'Skipped', 'uncanny-automator' ) ),
		icon: 'circle-minus',
		colorVariable: '--uap-log-status-skipped',
	},
	'not-completed': {
		label: escapeHTML( __( 'Not completed', 'uncanny-automator' ) ),
		icon: 'circle-minus',
		colorVariable: '--uap-log-status-not-completed',
	},
	queued: {
		label: escapeHTML( __( 'Queued', 'uncanny-automator' ) ),
		icon: 'hourglass',
		colorVariable: '--uap-log-status-queued',
	},
	'completed-with-errors': {
		label: escapeHTML( __( 'Completed with errors', 'uncanny-automator' ) ),
		icon: 'times-circle',
		colorVariable: '--uap-log-status-completed-with-errors',
	},
	'in-progress': {
		label: escapeHTML( __( 'In progress', 'uncanny-automator' ) ),
		icon: 'clock',
		colorVariable: '--uap-log-status-in-progress',
	},
	'in-progress-with-errors': {
		label: escapeHTML( __( 'In progress with errors', 'uncanny-automator' ) ),
		icon: 'clock',
		colorVariable: '--uap-log-status-in-progress-with-errors',
	},
	scheduled: {
		label: escapeHTML( __( 'Scheduled', 'uncanny-automator' ) ),
		icon: 'clock',
		colorVariable: '--uap-log-status-scheduled',
	},
	delayed: {
		label: escapeHTML( __( 'Delayed', 'uncanny-automator' ) ),
		icon: 'clock',
		colorVariable: '--uap-log-status-delayed',
	},
	failed: {
		label: escapeHTML( __( 'Failed', 'uncanny-automator' ) ),
		icon: 'times-circle',
		colorVariable: '--uap-log-status-failed',
	},
	'completed-awaiting': {
		label: escapeHTML( __( 'Completed awaiting', 'uncanny-automator' ) ),
		icon: 'clock',
		colorVariable: '--uap-log-status-completed-awaiting',
	},
	'condition-met': {
		label: escapeHTML( __( 'Met', 'uncanny-automator' ) ),
		icon: 'check-circle',
		colorVariable: '--uap-log-status-condition-met',
	},
	'condition-not-met': {
		label: escapeHTML( __( 'Not met', 'uncanny-automator' ) ),
		icon: 'times-circle',
		colorVariable: '--uap-log-status-condition-not-met',
	},
	'condition-not-evaluated': {
		label: escapeHTML( __( 'Not evaluated', 'uncanny-automator' ) ),
		icon: 'circle-minus',
		colorVariable: '--uap-log-status-condition-not-evaluated',
	},
}

// eslint-disable-next-line require-jsdoc
@customElement( 'uap-log-status' )
export class AutomatorLogStatus extends LitElement {
	// Define style
	static styles = css`${ unsafeCSS( styles ) }`;

	// Properties
	@property( { type: String, attribute: 'status-id' } ) accessor statusID;

	@property( { type: Number, attribute: 'count-number' } ) accessor countNumber;

	@property( { type: Boolean, attribute: 'hide-label' } ) accessor hideLabel = false;

	@property( { type: Boolean, attribute: 'show-tooltip' } ) accessor showTooltip = false;

	// eslint-disable-next-line require-jsdoc
	render() {

		// Check that the user is using a valid ID
		if ( ! isDefined( statusesData[ this.statusID ] ) ) {
			return html`${ sprintf(
				/* translators: 1. The status ID */
				escapeHTML( __( 'Error: "%1$s" is not a valid status ID.', 'uncanny-automator' ) ),
				this.statusID
			) }`;
		}

		if ( ! this.showTooltip ) {
			return this._templateStatus;
		}

		return html`
			<uo-tooltip>
				<span class="tooltip-nowrap">${ statusesData[ this.statusID ].label }</span>

				<div slot="target">${ this._templateStatus }</div>
			</uo-tooltip>
			
		`;
	}

	/**
	 * Generates a template for the status component.
	 *
	 * @return {TemplateResult} - A lit-html template result for the status component. The template includes a status icon, a count number (if not empty), and a label (if not hidden).
	 */
	get _templateStatus() {
		return html`
			<div class="status">
				<span class="status__icon" style="color: var(${ statusesData[ this.statusID ].colorVariable })">
					<uo-icon 
						id="${ statusesData[ this.statusID ].icon }"
						icon-style="solid"
					></uo-icon>
				</span>

				${
					! isEmpty( this.countNumber ) ?
					html`<span class="status__count">${ this.countNumber.toLocaleString() }</span>` : ''
				}

				${ ! this.hideLabel ? html`<span class="status__label">${ statusesData[ this.statusID ].label }</span>` : '' }
			</div>
		`
	}
}
